/* ---------------------------------------------------------------------------
   A bridge's state as a chip: planned, under construction, in service,
   restricted or closed (LIFECYCLE.md §3, `LC-10`).

   One drawing, used by the register's row, the field card, and the record's
   header, so the five states read the same everywhere. A bridge with no phase
   recorded says so — *In service · not recorded* on the record, and the same
   in the chip's title on the register, where a column of the mark would bury
   the rows that have one.
   --------------------------------------------------------------------------- */

import { tag, type TagRank } from './widgets.ts';
import {
  WORKS_KIND_LABEL,
  phaseLabel,
  phaseOf,
  stateKey,
  STATE_LABEL,
  type PhaseFields,
  type PhaseStateKey,
  type WorksOpen,
} from '../data/phase.ts';

/** On the accent ramp every rank in this application uses: 4 wants attention. */
const RANK: Readonly<Record<PhaseStateKey, TagRank>> = {
  planned: 'none',
  construction: 2,
  'in-service': 1,
  restricted: 4,
  closed: 4,
};

/**
 * The chip. `compact` drops the *not recorded* mark from the text and keeps it
 * in the title — for the register, one row per structure.
 */
export function phaseChip(b: PhaseFields & { phaseAt?: string | null; phaseBy?: string | null }, compact = false): HTMLSpanElement {
  const p = phaseOf(b);
  const key = stateKey(p);
  const title = p.recorded
    ? [STATE_LABEL[key], b.phaseAt ? `since ${b.phaseAt.slice(0, 10)}` : null, b.phaseBy ? `by ${b.phaseBy}` : null]
        .filter(Boolean)
        .join(' · ')
    : 'In service — no phase is recorded for this structure';
  const chip = tag(compact ? STATE_LABEL[key] : phaseLabel(p), p.recorded ? RANK[key] : 'none', title);
  chip.classList.add('tag--phase');
  chip.dataset['phase'] = p.recorded ? key : 'not-recorded';
  return chip;
}

/**
 * *Works open*, beside the state, where works are open on a bridge in service
 * (LC-81) — an overlay on the state, so a chip of its own. Null otherwise.
 */
export function worksChip(b: PhaseFields & { works?: WorksOpen | null }): HTMLSpanElement | null {
  const works = phaseOf(b).works;
  if (works === null) return null;
  const since = b.works?.since ? ` since ${b.works.since.slice(0, 10)}` : '';
  const chip = tag('Works open', 2, `${WORKS_KIND_LABEL[works]}${since}${b.works?.by ? ` · by ${b.works.by}` : ''}`);
  chip.dataset['works'] = works;
  return chip;
}
